import type * as THREE from 'three';
import { INCURSION_TIERS, type HeroClass } from '@somnhold/content';
import { createBattle, createIncursionMap, groupReached, stepBattle, type BattleState, type CampaignState, heroName } from '@somnhold/sim';
import { BattleView, Post, TableCamera } from '@somnhold/render';
import { audio } from '../audio/sfx.ts';
import { t } from '../i18n.ts';
import { settings } from '../settings.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { Loop } from '../game/loop.ts';

const key = (id: string): string => `somnhold.reveal.ascuas.${id}`;

/** Whether this campaign's group should see the Ascuas reveal (once, from the group level that opens it). */
export const ascuasRevealDue = (s: CampaignState): boolean => {
  if (s.status !== 'active' || !groupReached(s, INCURSION_TIERS[2].minGroup)) return false;
  try {
    return localStorage.getItem(key(s.id)) !== '1';
  } catch {
    return false;
  }
};

const markSeen = (id: string): void => {
  try {
    localStorage.setItem(key(id), '1');
  } catch {
    /* private mode: it may show again next time */
  }
};

/**
 * The second Marea opens the Ascuas: a short scene on a burning clearing — the group's heroes, the
 * lava, the eruptions and the magma lord rising — with what changed and the way to the incursions.
 */
export class AscuasReveal {
  readonly el: HTMLElement;
  private battle: BattleState;
  private view: BattleView;
  private cam = new TableCamera(window.innerWidth / window.innerHeight);
  private post: Post;
  private loop: Loop;
  private t0 = 0;
  private me: string;

  constructor(
    private renderer: THREE.WebGLRenderer,
    private s: CampaignState,
    me: string,
    onDone: (toIncursions: boolean) => void,
  ) {
    this.me = me;
    this.battle = this.make(me);
    this.view = new BattleView(this.battle.map, this.battle.seed, { quality: settings.quality, me });
    this.post = new Post(renderer, this.view.scene, this.cam.camera, settings.tiltShift);
    // On a phone the card takes the lower half: closer, and the clearing lifted into the upper half.
    const tall = window.innerWidth < window.innerHeight;
    this.cam.setDistance(tall ? 22 : 17, true);
    this.cam.rotate(0, -0.12);
    const done = (go: boolean): void => {
      markSeen(this.s.id);
      audio.play('click');
      onDone(go);
    };
    this.el = h(
      'div.game.ascuas-reveal',
      {},
      renderer.domElement,
      h(
        'div.ar-card',
        { role: 'dialog', 'aria-modal': 'true', 'data-test': 'ascuas-reveal' },
        h('span.ar-kicker', {}, t('reveal.kicker', { n: this.s.mareaIndex + 1 })),
        h('h2', {}, t('reveal.title')),
        h('p.ar-lead', {}, t('reveal.lead')),
        h(
          'ul.ar-points',
          {},
          h('li', {}, h('span', { html: ICONS.map }), t('reveal.p1')),
          h('li', {}, h('span', { html: ICONS.skull }), t('reveal.p2')),
          h('li', {}, h('span', { html: ICONS.ember }), t('reveal.p3')),
        ),
        h('div.ar-actions', {}, h('button.btn.ghost', { 'data-test': 'reveal-later', onclick: () => done(false) }, t('reveal.later')), h('button.btn', { 'data-test': 'reveal-go', onclick: () => done(true) }, t('reveal.go'))),
      ),
    );
    this.loop = new Loop(
      () => this.tick(),
      (dt) => this.frame(dt),
    );
    window.addEventListener('resize', this.resize);
    this.resize();
  }

  /** A burning clearing: the player's hero and two friends (as they are), a few nightmares, and the lord. */
  private make(me: string): BattleState {
    const seed = 4100 + (this.t0 % 7);
    const players = this.s.players.filter((p) => !p.left);
    const mine = players.find((p) => p.id === me);
    const cast = [mine, ...players.filter((p) => p.id !== me)].filter((p): p is NonNullable<typeof p> => !!p).slice(0, 3);
    const classes: HeroClass[] = cast.length ? cast.map((p) => p.hero.heroClass) : ['warrior', 'mage', 'cleric'];
    const map = createIncursionMap(seed);
    return createBattle({
      mode: 'incursion',
      seed,
      map,
      structures: [],
      heroes: classes.map((cls, i) => ({ pid: cast[i]?.id ?? `r${i}`, name: cast[i] ? heroName(cast[i]) : '', cls, level: Math.max(5, cast[i]?.hero.level ?? 5), control: 'ghost' as const, powerBp: 9000, post: i })),
      keepHp: 99999,
      keepMaxHp: 99999,
      marea: this.s.mareaIndex,
      wave: 0,
      spawns: [
        ...Array.from({ length: 10 }, (_, i) => ({ tick: 10 + i * 9, sector: i % 3, kind: (i % 3 ? 'goblin' : 'wolf') as 'goblin' | 'wolf', tier: 1 as const, elite: false })),
        // The lord rises right at the edge of the clearing, in sight from the first second.
        { tick: 12, sector: 1, kind: 'magmaLord' as const, tier: 1 as const, elite: false, at: { q: 3, r: -1 } },
      ],
      objectiveTicks: 99999,
      rules: { tier: 2, enemyHpBp: 30000 },
    });
  }

  private tick(): void {
    this.t0++;
    // Twenty seconds of it, then the clearing burns anew.
    if (this.t0 % 240 === 0) this.battle = this.make(this.me);
    stepBattle(this.battle);
    this.view.sync(this.battle);
  }

  private frame(dt: number): void {
    this.cam.rotate(dt * 0.07);
    this.cam.update(dt);
    this.view.frame(this.cam.camera);
    this.post.render();
  }

  private resize = (): void => {
    const pr = Math.min(window.devicePixelRatio || 1, 1.5);
    this.renderer.setPixelRatio(pr);
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.cam.resize(window.innerWidth / window.innerHeight);
    // On a phone the card covers the lower half: the scene is drawn higher, centred on the half above it.
    const w = window.innerWidth;
    const hh = window.innerHeight;
    if (w < hh) this.cam.camera.setViewOffset(w, hh, 0, Math.round(hh * 0.15), w, hh);
    else this.cam.camera.clearViewOffset();
    this.post.setSize(window.innerWidth, window.innerHeight, pr);
  };

  start(): void {
    for (let i = 0; i < 40; i++) stepBattle(this.battle);
    this.loop.start();
    (window as unknown as { __somnhold?: unknown }).__somnhold = { reveal: this, state: this.battle, cam: this.cam };
  }

  destroy(): void {
    this.loop.stop();
    window.removeEventListener('resize', this.resize);
    this.view.dispose();
  }
}
